import { BigNumber } from '@ethersproject/bignumber';
import { create } from 'zustand';

import { IPairWallet } from 'api/apiPairs/models';

export type TransferWizardStep = 'selectRecipients' | 'allocate';

type TransferTokenType = 'base' | 'quote' | 'fee' | 'native';

export interface TransferTokenOption {
  id: number;
  symbol: string;
  decimals: number;
  type: TransferTokenType;
}

export interface TransferRecipient {
  walletId: number;
  walletAddress: string;
  balanceWei: BigNumber;
  balanceFormatted: string;
  amount: string;
}

export interface AllocationErrors {
  total?: string;
  recipients: Record<number, string | undefined>;
}

interface TransferWizardState {
  step: TransferWizardStep;
  sourceWallet?: IPairWallet;
  token?: TransferTokenOption;
  totalAmount: string;
  recipients: TransferRecipient[];
  errors: AllocationErrors;
  isSubmitting: boolean;
  setSourceWallet: (wallet: IPairWallet | undefined) => void;
  setToken: (token: TransferTokenOption | undefined) => void;
  setStep: (step: TransferWizardStep) => void;
  setRecipients: (recipients: TransferRecipient[]) => void;
  updateRecipientAmount: (walletId: number, amount: string) => void;
  setTotalAmount: (amount: string) => void;
  setErrors: (errors: AllocationErrors) => void;
  setIsSubmitting: (flag: boolean) => void;
  reset: () => void;
}

const initialState: Pick<
  TransferWizardState,
  'step' | 'sourceWallet' | 'token' | 'totalAmount' | 'recipients' | 'errors' | 'isSubmitting'
> = {
  step: 'selectRecipients',
  sourceWallet: undefined,
  token: undefined,
  totalAmount: '',
  recipients: [],
  errors: { recipients: {} },
  isSubmitting: false,
};

export const useTransferWizardStore = create<TransferWizardState>((set, get) => ({
  ...initialState,
  setSourceWallet: wallet => set({ sourceWallet: wallet }),
  setToken: token => set({ token }),
  setStep: step => set({ step }),
  setRecipients: recipients => set({ recipients }),
  updateRecipientAmount: (walletId, amount) => {
    const recipients = get().recipients.map(recipient =>
      recipient.walletId === walletId ? { ...recipient, amount } : recipient,
    );

    set({ recipients });
  },
  setTotalAmount: amount => set({ totalAmount: amount }),
  setErrors: errors => set({ errors }),
  setIsSubmitting: flag => set({ isSubmitting: flag }),
  reset: () => set({ ...initialState }),
}));

export const resetTransferWizardState = () => useTransferWizardStore.getState().reset();
